import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  resource,
  ResourceStatus,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import type Stripe from 'stripe';
import { getMetadataField } from '../../../lib/get-metadata-field';
import { findSubscriptionLineItem } from '../../../lib/subscription-helpers';
import { CustomerService } from '../../../services/customer.service';
import { OrganizationService } from '../../../services/organization.service';
import { PricingService } from '../../../services/pricing.service';
import { ProrationService } from '../../../services/proration.service';
import { StateService } from '../../../services/state.service';
import { SubscriptionService } from '../../../services/subscription.service';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { CustomCurrencyPipe } from '../../../shared/pipes/custom-currency.pipe';
import { CheckoutMode } from '../../../types/checkout-mode';
import { Promotion } from '../../../types/promotion.type';
import { CheckoutProrationInfoComponent } from '../checkout-proration-info/checkout-proration-info.component';

@Component({
  selector: 'app-checkout-pricing-details',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './checkout-pricing-details.component.html',
  styleUrl: './checkout-pricing-details.component.scss',
  imports: [
    CustomCurrencyPipe,
    DatePipe,
    ContextMenuComponent,
    ButtonDirective,
    SvgComponent,
    CheckoutProrationInfoComponent,
    CurrencyPipe,
    LoadingIndicatorComponent,
  ],
})
export class CheckoutPricingDetailsComponent {
  private state = inject(StateService);
  private organizationService = inject(OrganizationService);
  private pricingService = inject(PricingService);
  private subscriptionService = inject(SubscriptionService);
  private prorationService = inject(ProrationService);
  private customerService = inject(CustomerService);
  price = input.required<Stripe.Price & { product: Stripe.Product }>();
  mode = input.required<CheckoutMode>();
  endTrialEarly = input.required<boolean>();
  promotion = input<Promotion>();
  activeTenant = toSignal(this.state.activeTenant$);
  subscription = toSignal(this.subscriptionService.getSubscription());
  prorationMode = computed(() => {
    const tenant = this.activeTenant();
    const sub = this.subscription();
    return !!tenant?.id_stripe_subscription && sub?.status !== 'trialing';
  });
  trialMode = computed(() => this.pricingService.canStartTrial(this.subscription()));
  trialDays = signal(this.pricingService.getTrialLengthDays());
  proration = resource({
    request: () => ({ price: this.price(), promotion: this.promotion(), prorationMode: this.prorationMode() }),
    loader: params => {
      if (!params.request.prorationMode) return;
      return firstValueFrom(this.prorationService.getProration(params.request.price, params.request.promotion));
    },
  });

  updateTrialMode = computed(() => !this.endTrialEarly() && this.subscription()?.status === 'trialing');
  trialEndDate = computed(() => {
    const sub = this.subscription();
    if (sub?.status === 'trialing') {
      return new Date(sub.trial_end * 1000);
    }
    return new Date(Date.now() + this.trialDays() * 24 * 60 * 60 * 1000);
  });
  members = toSignal(this.organizationService.getMembers());
  mainPrices = toSignal(this.pricingService.getPrices('main'));
  seatPrices = toSignal(this.pricingService.getPrices('seat_addon'));
  storagePrices = toSignal(this.pricingService.getPrices('storage_addon'));

  monthlyPrice = computed(() => {
    const mainPrices = this.mainPrices();
    const price = this.price();
    if (!mainPrices || !price) return;
    const productPrices = mainPrices.filter(mainPrice => (mainPrice.product as Stripe.Product).id === price.product.id);
    return this.pricingService.getDefaultPrice('month', productPrices) as Stripe.Price & { product: Stripe.Product };
  });

  seatPrice = computed(() => {
    const price = this.price();
    const seatPrices = this.seatPrices();
    if (!price || !seatPrices) return;
    const seatAddonPriceId = getMetadataField(price, 'seat_addon_price_id');
    return seatPrices.find(seatPrice => seatPrice.id === seatAddonPriceId);
  });

  monthlySeatPrice = computed(() => {
    const price = this.monthlyPrice();
    const seatPrices = this.seatPrices();
    if (!price || !seatPrices) return;
    const seatAddonPriceId = getMetadataField(price, 'seat_addon_price_id');
    return seatPrices.find(seatPrice => seatPrice.id === seatAddonPriceId);
  });

  additionalSeatsCount = computed(() => {
    const price = this.price();
    const members = this.members();
    if (!price || !members) return;
    const memberCount = members.length; // 10
    const newIncludedSeats = Number(getMetadataField(price, 'included_seat_count')); // 5
    const additionalSeatAddons = Math.max(0, memberCount - newIncludedSeats); // 5
    return additionalSeatAddons;
  });

  additionalSeatsAmount = computed(() => {
    const price = this.price();
    const seatPrice = this.seatPrice();
    const additionalSeats = this.additionalSeatsCount();
    const promotionPercent = this.promotion()?.percentOff || 0;
    if (!price || !seatPrice || additionalSeats === undefined) return;
    return additionalSeats * seatPrice.unit_amount * (1 - promotionPercent / 100);
  });

  additionalSeatsWorstCaseAmount = computed(() => {
    const price = this.monthlyPrice();
    const seatPrice = this.monthlySeatPrice();
    const additionalSeats = this.additionalSeatsCount();
    if (!price || !seatPrice || additionalSeats === undefined) return;
    return additionalSeats * seatPrice.unit_amount;
  });

  storagePrice = computed(() => {
    const storagePrices = this.storagePrices();
    const price = this.price();
    if (!price || !storagePrices) return;
    return this.pricingService.getDefaultPrice(price.recurring.interval, storagePrices);
  });

  monthlyStoragePrice = computed(() => {
    const storagePrices = this.storagePrices();
    if (!storagePrices) return;
    return this.pricingService.getDefaultPrice('month', storagePrices);
  });

  storageAddon = computed(() => findSubscriptionLineItem(this.subscription()?.items?.data, 'storage_addon'));
  storageCount = computed(() => this.storageAddon()?.quantity || 0);

  storageAmount = computed(() => {
    const storagePrice = this.storagePrice();
    const storageCount = this.storageCount();
    const promotionPercent = this.promotion()?.percentOff || 0;
    if (!storagePrice || storageCount === undefined) return;
    return storagePrice.unit_amount * storageCount * (1 - promotionPercent / 100);
  });

  storageWorstCaseAmount = computed(() => {
    const storagePrice = this.monthlyStoragePrice();
    const storageCount = this.storageCount();
    if (!storagePrice || storageCount === undefined) return;
    return storagePrice.unit_amount * storageCount;
  });

  showAdditionalRows = computed(() => {
    const additionalSeats = this.additionalSeatsCount();
    const additionalStorage = this.storageCount();
    return additionalSeats > 0 || additionalStorage > 0;
  });

  priceAmount = computed(() => {
    const price = this.price();
    const promotionPercent = this.promotion()?.percentOff || 0;
    if (!price) return;
    return price.unit_amount * (1 - promotionPercent / 100);
  });

  totalAmount = computed(() => {
    const price = this.price();
    const additionalSeatsAmount = this.additionalSeatsAmount();
    const storageAmount = this.storageAmount();
    if (!price || additionalSeatsAmount === undefined || storageAmount === undefined) return;
    const priceAmount = this.priceAmount();
    return priceAmount + additionalSeatsAmount + storageAmount;
  });

  worstCaseAmount = computed(() => {
    const price = this.price();
    const monthlyPrice = this.monthlyPrice();
    const additionalSeatsAmount = this.additionalSeatsWorstCaseAmount();
    const storageAmount = this.storageWorstCaseAmount();
    if (!monthlyPrice || additionalSeatsAmount === undefined || storageAmount === undefined) return;
    const monthlyTotal = monthlyPrice.unit_amount + additionalSeatsAmount + storageAmount;
    if (price.recurring.interval === 'month') {
      return monthlyTotal;
    }
    return monthlyTotal * 12;
  });

  private prorationTotal = computed(() => {
    if (this.proration.status() !== ResourceStatus.Resolved) return;
    const proration = this.proration.value();
    return proration.amount_due;
  });

  dueTodayAmountWithoutBalance = computed(() => {
    if (this.prorationMode()) return this.prorationTotal();
    const promotionAbsolute = this.promotion()?.amountOff || 0;
    const totalAmount = this.totalAmount();
    if (totalAmount === undefined) return;
    const total = totalAmount - promotionAbsolute;
    return Math.max(0, total);
  });

  balance = toSignal(this.customerService.getBalance());

  balanceToBeUsed = computed(() => {
    const balance = this.balance();
    const total = this.dueTodayAmountWithoutBalance();
    if (balance === undefined || total === undefined) return;
    return Math.min(balance, total);
  });

  remainingBalance = computed(() => {
    const balance = this.balance();
    const balanceToBeUsed = this.balanceToBeUsed();
    if (balance === undefined || balanceToBeUsed === undefined) return;
    return balance - balanceToBeUsed;
  });

  dueTodayAmount = computed(() => {
    if (this.prorationMode()) return this.prorationTotal();
    if (this.trialMode() || this.updateTrialMode()) return 0;
    const balanceToBeUsed = this.balanceToBeUsed();
    const totalAmount = this.dueTodayAmountWithoutBalance();
    if (balanceToBeUsed === undefined || totalAmount === undefined) return;
    return totalAmount - balanceToBeUsed;
  });
}
